import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, ArrowLeft, AlertTriangle, Printer, Download, Eye, Truck as TruckIcon, AlertCircle, FileText, ChevronDown, Paperclip } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { SalesOrderForm } from './SalesOrderForm'
import { SalesOrderFormValues } from './schema'
import { 
  getSalesOrder, 
  createSalesOrder, 
  updateSalesOrder
} from '@/app/services/salesOrderService'
import { getParty } from '@/app/services/partyService'
import { listSites } from '@/app/services/siteService'
import { listItems } from '@/app/services/itemService'
import { listGodowns } from '@/app/services/inventoryService'
import { Item } from '@/app/types/item'
import { Party } from '@/app/types/party'
import { Badge, BadgeProps } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { format } from 'date-fns'
import { pdf } from '@react-pdf/renderer'
import { SalesOrderPDFTemplate } from '@/app/components/SalesOrderPDFTemplate'
import { getDeliveryChallansForSalesOrder } from '@/app/services/documentService'
import { 
  getSalesOrderPrintData, 
  generateSalesOrderHTML, 
  generateSalesOrderPDF,
  previewSalesOrderPDF 
} from '@/app/services/salesOrderPrintService'
import { getInvoiceBySalesOrder } from '@/app/services/documentLinkService'
import { LinkedInvoiceModal } from '../components/LinkedInvoiceModal'
import { DocumentAttachments } from '@/app/components/DocumentAttachments'
import { attachmentService } from '@/app/services/attachmentService'
import { getCompanyDetailsForPrint, CompanyDetails } from '@/app/config/branding'

const SalesOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditMode = !!id
  
  // State for item search
  const [isSearchingItems, setIsSearchingItems] = useState(false)
  
  // State for PDF operations
  const [isPdfLoading, setIsPdfLoading] = useState(false)
  
  // State for linked invoice modal
  const [isLinkedInvoiceModalOpen, setIsLinkedInvoiceModalOpen] = useState(false)
  
  // State for documents modal
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false)

  // Company details for PDF - from centralized branding config
  const companyDetails = getCompanyDetailsForPrint()

  // Function to search items for product selection in the grid
  const searchItems = async (query: string): Promise<Item[]> => {
    if (!query || query.length < 2) return []
    
    setIsSearchingItems(true)
    try {
      const result = await listItems({ search_query: query, limit: 20 })
      return result.items || []
    } catch (error) {
      console.error('Error searching items:', error)
      return []
    } finally {
      setIsSearchingItems(false)
    }
  }

  // Fetch sales order if in edit mode
  const { 
    data: salesOrder, 
    isLoading: isLoadingSalesOrder, 
    isError: isErrorSalesOrder,
    error: errorSalesOrder
  } = useQuery({
    queryKey: ['sales-order', id],
    queryFn: () => getSalesOrder(Number(id)),
    enabled: isEditMode,
  })

  // Check if the order is in a finalized state
  const isOrderFinalized = (salesOrder as any)?.is_cancelled || 
    salesOrder?.status === 'Delivered' || 
    salesOrder?.status === 'Invoiced'

  // Fetch party details if we have a party_id from the sales order
  const partyId = salesOrder?.party_id
  const {
    data: partyDetails,
    isLoading: isLoadingPartyDetails,
  } = useQuery<Party | null>({
    queryKey: ['party-details', partyId],
    queryFn: () => partyId ? getParty(partyId) : null,
    enabled: !!partyId,
  })

  // Fetch sites and other needed data
  const { data: sitesData = { items: [] }, isLoading: isLoadingSites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 })
  })

  // Fetch godowns (warehouses) for item selection
  const { data: godownsData = { items: [] }, isLoading: isLoadingGodowns } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 100 })
  })

  // Fetch linked invoice if exists
  const { data: linkedInvoice } = useQuery({
    queryKey: ['linked-invoice', id],
    queryFn: () => id ? getInvoiceBySalesOrder(Number(id)) : null,
    enabled: isEditMode && !!id
  })

  // Fetch attachments count for the sales order
  const { data: attachments } = useQuery({
    queryKey: ['attachments', 'sales_order', id],
    queryFn: () => id ? attachmentService.getEntityAttachments('sales_order', Number(id)) : [],
    enabled: isEditMode && !!id
  })


  // Create sales order mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => createSalesOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] })
      toast.success('Sales order created successfully')
      navigate('/sales-orders/list')
    },
    onError: (error: Error) => {
      toast.error(`Failed to create sales order: ${error.message}`)
    }
  })

  // Update sales order mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => {
      console.log('Starting update mutation with ID:', id)
      return updateSalesOrder(id, data)
    },
    onSuccess: (data) => {
      console.log('Update mutation succeeded, result:', data)
      console.log('Current ID from useParams:', id)
      
      // First invalidate queries and show success
      console.log('Invalidating queries...')
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] })
      queryClient.invalidateQueries({ queryKey: ['sales-order', id] })
      
      console.log('Showing success toast')
      toast.success('Sales order updated successfully')
      
      console.log(`About to navigate to: /sales-orders/detail/${id}`)
      
      // Use a slightly longer timeout to ensure all operations complete
      setTimeout(() => {
        console.log(`Navigation via replace to: /sales-orders/detail/${id}`)
        
        // Use a more forceful redirect with replace
        if (id && id.toString().trim().length > 0) {
          console.log(`ID is valid: "${id}", forcefully replacing location...`)
          window.location.replace(`#/sales-orders/detail/${id}`)
        } else {
          console.error(`Invalid ID for navigation: "${id}"`)
          window.location.replace('#/sales-orders/list')
        }
      }, 300) // Use a longer timeout to ensure completion
    },
    onError: (error: Error) => {
      console.error('Update mutation failed with error:', error)
      toast.error(`Failed to update sales order: ${error.message}`)
    }
  })

  // Convert to invoice mutation - removed as we'll handle it differently

  // Handle form submission
  const handleSubmit = (data: SalesOrderFormValues): void => {
    if (isOrderFinalized) {
      toast.error('Cannot update a finalized sales order')
      return
    }

    try {
      // Convert form data to API format
      const apiData = {
        site_id: data.site_id,
        party_id: data.party_id,
        reference: data.reference_number, // API expects 'reference' not 'reference_number'
        notes: data.narration, // Convert narration to notes
        // Convert dates to YYYY-MM-DD format for API
        transaction_date: data.transaction_date instanceof Date 
          ? format(data.transaction_date, 'yyyy-MM-dd')
          : data.transaction_date,
        expected_delivery_date: data.expected_delivery_date instanceof Date 
          ? format(data.expected_delivery_date, 'yyyy-MM-dd')
          : data.expected_delivery_date,
        status: 'draft',
        // Process items to ensure they have all required fields
        items: data.items.map((item) => ({
          item_id: item.item_id,
          description: item.description || '',
          quantity: item.quantity,
          unit_id: item.unit_id || 1, // Use unit_id from item or default to 1
          rate: item.rate,
          discount_percent: item.discount_percent || 0,
          discount_amount: item.discount_amount || 0,
          tax_rate_id: item.tax_rate_id,
          godown_id: item.godown_id,
          // Remove calculated fields - let backend calculate
          // taxable_amount, tax_amount, total_amount are calculated by backend
        }))
      }
      
      console.log('Submitting sales order data:', JSON.stringify(apiData, null, 2))
      
      if (isEditMode && id) {
        updateMutation.mutate({ id: Number(id), data: apiData })
      } else {
        createMutation.mutate(apiData)
      }
    } catch (error) {
      console.error('Error during submission:', error)
      toast.error(`Submission error: ${(error as Error).message}`)
    }
  }


  // Handle errors consistently
  useEffect(() => {
    if (isErrorSalesOrder) {
      toast.error(`Error loading sales order: ${(errorSalesOrder as Error).message}`)
    }
  }, [isErrorSalesOrder, errorSalesOrder])

  // Save PDF - Web implementation
  const handleSavePDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      
      if (!salesOrder) {
        throw new Error('No sales order data available')
      }
      
      console.log('Generating PDF with data:', {
        salesOrder,
        partyDetails,
        companyDetails
      })
      
      // Generate PDF using original template
      const pdfDoc = (
        <SalesOrderPDFTemplate
          salesOrder={salesOrder}
          partyDetails={partyDetails || undefined}
          companyDetails={companyDetails}
        />
      )
      
      const blob = await pdf(pdfDoc).toBlob()
      
      // Force download instead of opening in browser
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `SalesOrder-${salesOrder.document_number || salesOrder.so_number || 'DRAFT'}.pdf`)
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('PDF downloaded successfully')
    } catch (error) {
      console.error('Error saving PDF:', error)
      toast.error(`Failed to save PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }
  
  // Print PDF - Web implementation
  const handlePrintPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      
      if (!salesOrder) throw new Error('No sales order data available')
      
      // Generate PDF and open for printing
      const pdfDoc = (
        <SalesOrderPDFTemplate
          salesOrder={salesOrder}
          partyDetails={partyDetails || undefined}
          companyDetails={companyDetails}
        />
      )
      
      const blob = await pdf(pdfDoc).toBlob()
      const url = URL.createObjectURL(blob)
      
      // Open in new window and trigger print
      const printWindow = window.open(url, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }
      
      // Clean up after delay
      setTimeout(() => URL.revokeObjectURL(url), 10000)
      
      toast.success('Print dialog opened')
    } catch (error) {
      console.error('Error printing:', error)
      toast.error(`Failed to print: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }
  
  // Preview PDF V2 - Web implementation
  const handlePreviewPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      
      if (!salesOrder) throw new Error('No sales order data available')
      
      // Generate PDF with original template and open in new tab
      const pdfDoc = (
        <SalesOrderPDFTemplate
          salesOrder={salesOrder}
          partyDetails={partyDetails || undefined}
          companyDetails={companyDetails}
        />
      )
      
      const blob = await pdf(pdfDoc).toBlob()
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      
      // Clean up after a delay
      setTimeout(() => URL.revokeObjectURL(url), 10000)
      
      toast.success('PDF preview opened in new tab')
    } catch (error) {
      console.error('PDF preview error:', error)
      toast.error(`Failed to preview: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Preview HTML - Web implementation using HTML template from backend
  const handlePreviewHTML = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      
      if (!salesOrder || !id) throw new Error('No sales order data available')
      
      // Fetch print data from backend
      const printData = await getSalesOrderPrintData(Number(id))
      
      // Generate HTML
      const htmlContent = generateSalesOrderHTML(printData, false)
      
      // Open in new window
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(htmlContent)
        printWindow.document.close()
      }
      
      toast.success('HTML preview opened in new tab')
    } catch (error) {
      console.error('HTML preview error:', error)
      toast.error(`Failed to preview: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Preview HTML as PDF
  const handlePreviewHTMLPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      
      if (!salesOrder || !id) throw new Error('No sales order data available')
      
      // Fetch print data from backend
      const printData = await getSalesOrderPrintData(Number(id))
      
      // Generate and preview PDF
      await previewSalesOrderPDF(printData)
      
      toast.success('PDF preview opened in new tab')
    } catch (error) {
      console.error('PDF preview error:', error)
      toast.error(`Failed to preview PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Save HTML as PDF
  const handleSaveHTMLPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      
      if (!salesOrder || !id) throw new Error('No sales order data available')
      
      // Fetch print data from backend
      const printData = await getSalesOrderPrintData(Number(id))
      
      // Generate and save PDF
      await generateSalesOrderPDF(printData)
      
      toast.success('PDF saved successfully')
    } catch (error) {
      console.error('PDF save error:', error)
      toast.error(`Failed to save PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }


  // Handle convert to invoice - similar to duplicate invoice
  const handleConvertToInvoice = () => {
    if (!id || !salesOrder) {
      toast.error('No sales order available to convert')
      return
    }

    if (salesOrder.status === 'Invoiced') {
      toast.error('This sales order has already been converted to an invoice')
      return
    }

    if ((salesOrder as any)?.is_cancelled) {
      toast.error('Cannot convert a cancelled sales order to invoice')
      return
    }

    // Check if already linked to an invoice
    if (linkedInvoice) {
      toast.warning(`This order is already linked to Invoice #${linkedInvoice.voucher_number}`)
      setIsLinkedInvoiceModalOpen(true)
      return
    }

    // Prepare sales order data for conversion to invoice
    const invoiceData = {
      party_id: salesOrder.party_id,
      site_id: salesOrder.site_id,
      reference_number: salesOrder.reference || '', // Use sales order reference
      narration: `Converted from Sales Order #${salesOrder.so_number}`,
      items: salesOrder.items?.map(item => ({
        ...item,
        // Ensure all required fields are present for invoice items
        item_id: item.item_id,
        description: item.description || '',
        quantity: item.quantity,
        unit_id: item.unit_id || 1,
        rate: item.rate,
        discount_percent: item.discount_percent || 0,
        discount_amount: item.discount_amount || 0,
        tax_rate_id: item.tax_rate_id,
        godown_id: item.godown_id,
      })) || [],
      // Use current date for invoice
      transaction_date: new Date(),
      due_date: null,
      // You may need to map voucher_type_id for sales invoice
      voucher_type_id: 1, // Assuming 1 is for sales invoice, adjust as needed
      total_amount: salesOrder.total_amount,
    }
    
    // Navigate to create invoice page with the converted data, including SO ID for linking
    navigate('/sales-invoices/new', { 
      state: { 
        duplicateInvoice: invoiceData,
        fromDuplicate: true,
        fromSalesOrder: true,
        salesOrderId: Number(id), // Pass the SO ID for linking after creation
        salesOrderNumber: salesOrder.so_number
      } 
    })
    toast.info('Sales order data loaded. Review and save the invoice.')
  }

  // Loading state
  const isLoading =
    isLoadingSalesOrder ||
    isLoadingSites ||
    isLoadingGodowns ||
    isLoadingPartyDetails
  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const canGeneratePdf = isEditMode && salesOrder && !isLoading

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/sales-orders/list')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode ? `Edit Sales Order ${salesOrder?.so_number}` : 'Create Sales Order'}
          </h1>
        </div>
        
        {/* PDF Action Buttons */}
        {canGeneratePdf && (
          <div className="flex items-center gap-2">
            {/* Documents Button */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsDocumentsModalOpen(true)}
            >
              <Paperclip className="h-4 w-4 mr-2" />
              Documents
              {attachments && attachments.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {attachments.length}
                </Badge>
              )}
            </Button>
            
            {/* Show linked invoice button if exists */}
            {linkedInvoice && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsLinkedInvoiceModalOpen(true)}
              >
                <FileText className="h-4 w-4 mr-2" />
                View Invoice #{linkedInvoice.voucher_number}
              </Button>
            )}
            
            {/* Convert to Invoice Button - only show if not linked */}
            {!isOrderFinalized && salesOrder?.status !== 'Invoiced' && !linkedInvoice && (
              <Button 
                variant="default" 
                size="sm" 
                onClick={handleConvertToInvoice}
              >
                <FileText className="h-4 w-4 mr-2" />
                Convert to Invoice
              </Button>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isPdfLoading}>
                  {isPdfLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Printer className="h-4 w-4 mr-2" />
                  )}
                  Print Options
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={handlePreviewHTML} 
                  disabled={true}
                  className="opacity-50 cursor-not-allowed"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Preview (HTML Format)
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handlePreviewHTMLPDF} 
                  disabled={isPdfLoading || !salesOrder}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Preview (HTML as PDF)
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleSaveHTMLPDF} 
                  disabled={isPdfLoading || !salesOrder}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Save (HTML as PDF)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {isErrorSalesOrder && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading sales order: {(errorSalesOrder as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {godownsData.items.length === 0 && !isLoadingGodowns && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No warehouses found. Please create at least one warehouse before creating sales orders.
          </AlertDescription>
        </Alert>
      )}

      {/* Show warning if order is finalized */}
      {isOrderFinalized && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This sales order has been{' '}
            {(salesOrder as any)?.is_cancelled
              ? 'cancelled'
              : salesOrder?.status === 'Delivered'
                ? 'delivered'
                : 'invoiced'}{' '}
            and cannot be modified.
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : godownsData.items.length > 0 ? (
        <SalesOrderForm
          key={id || 'new'} // Force re-render when ID changes
          salesOrder={salesOrder}
          sites={sitesData.items}
          godowns={godownsData.items}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          searchItems={searchItems}
          isLoadingItems={isSearchingItems}
          isDisabled={isOrderFinalized}
        />
      ) : null}
      
      {/* Linked Invoice Modal */}
      {isEditMode && salesOrder && (
        <LinkedInvoiceModal
          isOpen={isLinkedInvoiceModalOpen}
          onClose={() => setIsLinkedInvoiceModalOpen(false)}
          salesOrderId={Number(id)}
          salesOrderNumber={salesOrder.so_number}
        />
      )}
      
      {/* Documents Modal */}
      {isEditMode && id && (
        <Dialog open={isDocumentsModalOpen} onOpenChange={setIsDocumentsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Sales Order Documents
                {salesOrder?.so_number && (
                  <span className="ml-2 text-sm text-muted-foreground">
                    (Order #{salesOrder.so_number})
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <DocumentAttachments
                entityType="sales_order"
                entityId={Number(id)}
                title=""
                maxFileSize={10}
                maxFiles={20}
                enableMetadata={false}
                className="border-0 shadow-none"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export default SalesOrderDetailPage 